import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  alpha,
  useTheme
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { IResourceComponentsProps } from "@refinedev/core";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Download, Print, Search } from "@mui/icons-material";
import { Currency } from "react-intl-number-format";
import { getReceiptReports, ReceiptReportsParams, ReceiptReportsResponse } from "services/receipt";
import { BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";

export const ReceiptsReport: React.FC<IResourceComponentsProps> = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  // State for filters
  const [startDate, setStartDate] = useState<Date | null>(new Date(new Date().getFullYear(), 0, 1)); // Default to start of current year
  const [endDate, setEndDate] = useState<Date | null>(new Date()); // Default to current date
  const [clientFilter, setClientFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [paymentMethodFilter] = useState<string>("");
  const [groupBy, setGroupBy] = useState<string>("month");
  
  // State for API data
  const [loading, setLoading] = useState<boolean>(false);
  const [reportData, setReportData] = useState<ReceiptReportsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Function to fetch report data
  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params: ReceiptReportsParams = {
        from: startDate ? startDate.toISOString() : undefined,
        to: endDate ? endDate.toISOString() : undefined,
        groupBy: groupBy as any,
        clientId: clientFilter || undefined,
        status: statusFilter as any || undefined,
        paymentMethod: paymentMethodFilter || undefined
      };
      
      const data = await getReceiptReports(params);
      setReportData(data);
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError('Failed to load report data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    void fetchReportData();
    // Intentionally mount-only; filters applied via Search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle search button click
  const handleSearch = () => {
    fetchReportData();
  };

  
  
  const summary = reportData?.summary || {
    totalReceipts: 0,
    totalAmount: 0,
    totalReceived: 0,
    totalBalance: 0,
    paidCount: 0,
    unpaidCount: 0
  };
  
  const receiptData = reportData?.data || [];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">{t("Receipts Report")}</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={() => console.log("Print report")}
          >
            {t("Print")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => console.log("Export report")}
          >
            {t("Export")}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <DatePicker
              label={t("Start Date")}
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <DatePicker
              label={t("End Date")}
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t("Client ID")}
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("Status")}</InputLabel>
              <Select
                value={statusFilter}
                label={t("Status")}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">{t("All")}</MenuItem>
                <MenuItem value="paid">{t("Paid")}</MenuItem>
                <MenuItem value="unpaid">{t("Unpaid")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("Group By")}</InputLabel>
              <Select
                value={groupBy}
                label={t("Group By")}
                onChange={(e) => setGroupBy(e.target.value)}
              >
                <MenuItem value="">{t("None")}</MenuItem>
                <MenuItem value="day">{t("Day")}</MenuItem>
                <MenuItem value="month">{t("Month")}</MenuItem>
                <MenuItem value="client">{t("Client")}</MenuItem>
                <MenuItem value="payment_method">{t("Payment Method")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Search />}
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? t("Loading...") : t("Search")}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Error message */}
      {error && (
        <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                {t("Total Receipts")}
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {summary.totalReceipts}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                {t("Total Amount")}
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                <Currency locale="en-US" currency="TND">
                  {summary.totalAmount}
                </Currency>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                {t("Received Amount")}
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                <Currency locale="en-US" currency="TND">
                  {summary.totalReceived}
                </Currency>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                {t("Balance")}
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="warning.main">
                <Currency locale="en-US" currency="TND">
                  {summary.totalBalance}
                </Currency>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Charts */}
      {!loading && reportData && (
        <Grid container spacing={3} mb={3}>
          {/* Pie Chart - Paid vs Unpaid */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom align="center">
                {t("Payment Status Distribution")}
              </Typography>
              <Box height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: t('Paid'), value: summary.paidCount },
                        { name: t('Unpaid'), value: summary.unpaidCount }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }: { name: string, percent: number }) => 
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      <Cell fill={theme.palette.success.main} />
                      <Cell fill={theme.palette.warning.main} />
                    </Pie>
                    <Tooltip
                      formatter={(value: number) => [value, t('Receipts')]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
          
          {/* Bar Chart - Amount Comparison */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom align="center">
                {t("Amount Comparison")}
              </Typography>
              <Box height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      {
                        name: t('Amounts'),
                        total: summary.totalAmount,
                        received: summary.totalReceived,
                        balance: summary.totalBalance
                      }
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value: number) => [
                        <Currency locale="en-US" currency="TND">{value}</Currency>,
                        ''
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="total" name={t('Total Amount')} fill={theme.palette.primary.main} />
                    <Bar dataKey="received" name={t('Received')} fill={theme.palette.success.main} />
                    <Bar dataKey="balance" name={t('Balance')} fill={theme.palette.warning.main} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
          
          {/* Line Chart for grouped data */}
          {groupBy === 'month' && receiptData.length > 0 && (
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom align="center">
                  {t("Monthly Trend")}
                </Typography>
                <Box height={350}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={receiptData.map((month: any) => ({
                        name: month.month,
                        total: month.totalAmount,
                        received: month.totalReceived,
                        balance: month.totalBalance,
                        count: month.receipts?.length || 0
                      }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip 
                        formatter={(value: number, name: string) => {
                          if (name === t('Receipt Count')) return [value, name];
                          return [
                            <Currency locale="en-US" currency="TND">{value}</Currency>,
                            name
                          ];
                        }}
                      />
                      <Legend />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="total" 
                        name={t('Total Amount')} 
                        stroke={theme.palette.primary.main} 
                        activeDot={{ r: 8 }} 
                      />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="received" 
                        name={t('Received')} 
                        stroke={theme.palette.success.main} 
                      />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="balance" 
                        name={t('Balance')} 
                        stroke={theme.palette.warning.main} 
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="count" 
                        name={t('Receipt Count')} 
                        stroke={theme.palette.info.main} 
                        strokeDasharray="5 5"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          )}
          
          {/* Bar Chart for client data */}
          {groupBy === 'client' && receiptData.length > 0 && (
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom align="center">
                  {t("Client Comparison")}
                </Typography>
                <Box height={Math.max(350, receiptData.length * 40)}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={receiptData.map((client: any) => ({
                        name: client.clientName,
                        total: client.totalAmount,
                        received: client.totalReceived,
                        balance: client.totalBalance
                      }))}
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={80} />
                      <Tooltip 
                        formatter={(value: number) => [
                          <Currency locale="en-US" currency="TND">{value}</Currency>,
                          ''
                        ]}
                      />
                      <Legend />
                      <Bar dataKey="total" name={t('Total Amount')} fill={theme.palette.primary.main} />
                      <Bar dataKey="received" name={t('Received')} fill={theme.palette.success.main} />
                      <Bar dataKey="balance" name={t('Balance')} fill={theme.palette.warning.main} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Receipts List */}
      <Paper elevation={3}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            {t("Receipts")}
          </Typography>
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : receiptData.length === 0 ? (
            <Typography align="center" color="textSecondary" p={4}>
              {t("No receipts found matching the criteria")}
            </Typography>
          ) : (
            // If we have grouped data, render differently based on groupBy
            groupBy ? (
              <Box>
                {receiptData.map((group: any, index) => (
                  <Paper 
                    key={index}
                    elevation={1}
                    sx={{
                      p: 2,
                      mb: 3,
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      {/* Display appropriate group title based on groupBy */}
                      {groupBy === 'day' && group.date}
                      {groupBy === 'month' && group.month}
                      {groupBy === 'client' && group.clientName}
                      {groupBy === 'payment_method' && group.paymentMethod}
                    </Typography>
                    
                    <Grid container spacing={2} mb={2}>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="textSecondary">
                          {t("Total Receipts")}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {group.receipts?.length || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="textSecondary">
                          {t("Total Amount")}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          <Currency locale="en-US" currency="TND">
                            {group.totalAmount || 0}
                          </Currency>
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="textSecondary">
                          {t("Received Amount")}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="success.main">
                          <Currency locale="en-US" currency="TND">
                            {group.totalReceived || 0}
                          </Currency>
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="textSecondary">
                          {t("Balance")}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="warning.main">
                          <Currency locale="en-US" currency="TND">
                            {group.totalBalance || 0}
                          </Currency>
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="textSecondary">
                          {t("Paid")}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="success.main">
                          {group.paidCount || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="textSecondary">
                          {t("Unpaid")}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="warning.main">
                          {group.unpaidCount || 0}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    {/* If it's a month group with days, show the days */}
                    {groupBy === 'month' && group.days && (
                      <Box mt={3}>
                        <Typography variant="subtitle1" gutterBottom>
                          {t("Daily Breakdown")}
                        </Typography>
                        <Grid container spacing={2}>
                          {group.days.map((day: any, dayIndex: number) => (
                            <Grid item xs={12} md={6} lg={4} key={dayIndex}>
                              <Paper 
                                elevation={1} 
                                sx={{ 
                                  p: 2, 
                                  borderLeft: `3px solid ${theme.palette.secondary.main}` 
                                }}
                              >
                                <Typography variant="subtitle2">{day.date}</Typography>
                                <Box display="flex" justifyContent="space-between" mt={1}>
                                  <Typography variant="body2">
                                    {t("Receipts")}: {day.receipts?.length || 0}
                                  </Typography>
                                  <Typography variant="body2">
                                    <Currency locale="en-US" currency="TND">
                                      {day.totalAmount || 0}
                                    </Currency>
                                  </Typography>
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            ) : (
              // If no grouping, render individual receipts
              receiptData.map((receipt: any) => (
                <Paper 
                  key={receipt.id}
                  elevation={1}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderLeft: `4px solid ${
                      receipt.status === "paid" 
                        ? theme.palette.success.main 
                        : theme.palette.warning.main
                    }`,
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        {t("Receipt Number")}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {receipt.receiptNumber}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        {t("Date")}
                      </Typography>
                      <Typography variant="body1">
                        {new Date(receipt.date).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        {t("Client")}
                      </Typography>
                      <Typography variant="body1">
                        {receipt.clientName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        {t("Payment Method")}
                      </Typography>
                      <Typography variant="body1" textTransform="capitalize">
                        {receipt.paymentMethod}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <Typography variant="body2" color="textSecondary">
                        {t("Given")}
                      </Typography>
                      <Typography variant="body1">
                        <Currency locale="en-US" currency="TND">
                          {receipt.amountGiven}
                        </Currency>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <Typography variant="body2" color="textSecondary">
                        {t("Received")}
                      </Typography>
                      <Typography variant="body1">
                        <Currency locale="en-US" currency="TND">
                          {receipt.amountReceived}
                        </Currency>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <Typography variant="body2" color="textSecondary">
                        {t("Balance")}
                      </Typography>
                      <Typography variant="body1" color={receipt.balance > 0 ? "warning.main" : "success.main"}>
                        <Currency locale="en-US" currency="TND">
                          {receipt.balance}
                        </Currency>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <Typography
                        variant="body2"
                        sx={{
                          textTransform: "uppercase",
                          fontWeight: "bold",
                          color: receipt.status === "paid" 
                            ? "success.main" 
                            : "warning.main",
                          textAlign: "center",
                          p: 0.5,
                          borderRadius: 1,
                          bgcolor: receipt.status === "paid" 
                            ? alpha(theme.palette.success.main, 0.1)
                            : alpha(theme.palette.warning.main, 0.1),
                        }}
                      >
                        {t(receipt.status)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ))
            )
          )}
        </Box>
      </Paper>
    </Box>
  );
};
